import type { FeatureIconName } from './FeatureIcon';

/** Canonical event-type identities from the iOS app (EventType.systemImageName +
 *  DefaultThemeService event roles). Colors are constant across app themes;
 *  the website mirrors them so event types carry the same meaning in both places.
 *  Each type also keeps a distinct symbol — color never carries meaning alone. */
export type EventTypeId = 'cruise' | 'hangout' | 'tour' | 'workshop';

export interface EventTypeIdentity {
  /** Accent color for text/symbol use on neutral surfaces (>= 3:1 in both themes). */
  color: string;
  /** Closest Feather-family equivalent of the app's SF Symbol. */
  icon: FeatureIconName;
}

export const EVENT_TYPES: Record<EventTypeId, EventTypeIdentity> = {
  // .eventScenic -> systemBlue / road.lanes
  cruise:   { color: '#0077FF', icon: 'road' },
  // .eventSocial -> systemGreen / person.3.fill
  hangout:  { color: '#10b981', icon: 'users' },
  // .eventAdventure -> systemOrange / mountain.2.fill
  tour:     { color: '#f59e0b', icon: 'mountain' },
  // .eventWorkshop -> systemBrown / wrench.and.screwdriver.fill
  workshop: { color: '#A2845E', icon: 'wrench' },
};

/** Achievement-tier colors from iOS GameAchievement.difficultyColor:
 *  bronze systemOrange, silver systemGray, gold systemYellow, platinum systemPurple. */
export const TIER_COLORS = {
  bronze: '#d97706',
  silver: '#6E6E73',
  gold: '#ca8a04',
  platinum: '#8b5cf6',
} as const;

/** Premium gold is reserved for actual Pro membership and Pro features.
 *  Matches the app's premium tier (bright gold on dark, darkened on light). */
export const premiumGold = (theme: 'dark' | 'light'): string =>
  theme === 'dark' ? '#FFCC00' : '#8C6500';
